import { Field } from 'formik'
import { electronicFormatIBAN, friendlyFormatIBAN } from 'ibantools'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { isBICRequired } from '@lib/validators/iban'
import { SepaFormData } from '@lib/validators/sepa'
import InputField from '@ui/Input/Field'

import '@pages/Checkout/Payment/SEPAForm/index.scss'

const IBAN_MAX_LENGTH = 34
const BIC_MAX_LENGTH = 11

interface SEPAFormProps {
  data: SepaFormData
  onChange: (data: SepaFormData) => void
  errors?: Record<string, string>
  disabled?: boolean
}

export const SEPAForm: React.FC<SEPAFormProps> = ({ data, onChange, disabled = false }) => {
  const { t } = useTranslation()

  const cleanIBAN = electronicFormatIBAN(data.iban)
  const bicRequirement = cleanIBAN ? isBICRequired(cleanIBAN) : false
  const bicDisabled = disabled || (!bicRequirement && !data.iban)

  return (
    <div className="sepa-form">
      <div className="sepa-form__fields">
        <div className="sepa-form__field">
          <Field
            name="sepaData.iban"
            component={InputField}
            label={t('subscription.sepa.iban')}
            placeholder={t('subscription.sepa.placeholders.iban')}
            disabled={disabled}
            maxLength={IBAN_MAX_LENGTH}
            onChange={(value: string) => {
              const formattedIBAN = friendlyFormatIBAN(value) || value
              onChange({
                ...data,
                iban: formattedIBAN,
              })
            }}
          />
        </div>

        <div className="sepa-form__field">
          <Field
            name="sepaData.bic"
            component={InputField}
            label={t('subscription.sepa.bic')}
            placeholder={t('subscription.sepa.placeholders.bic')}
            disabled={bicDisabled}
            maxLength={BIC_MAX_LENGTH}
            onChange={(value: string) => {
              const upperBIC = value.toUpperCase()
              onChange({
                ...data,
                bic: upperBIC,
              })
            }}
          />
          {!bicRequirement && data.iban && (
            <div className="sepa-form__field-hint">{t('subscription.sepa.bicOptional')}</div>
          )}
        </div>

        <div className="sepa-form__field">
          <Field
            name="sepaData.holderName"
            component={InputField}
            label={t('subscription.sepa.accountHolderName')}
            placeholder={t('subscription.sepa.placeholders.accountHolderName')}
            disabled={disabled}
            onChange={(value: string) => {
              onChange({
                ...data,
                holderName: value,
              })
            }}
          />
        </div>
      </div>
    </div>
  )
}
